// ── types ───────────────────────────────────────────────────
export type UpdateType = 'blocked' | 'unfrozen' | 'replace'
export type ProcessingResult = 'applied' | 'skipped'

export const REQUIRED_COLUMNS: ReadonlyArray<string> = [
    'Existing Token',
    'Replace with Token',
    'Expiry Date',
    'Terminal',
    'Update Type',
    'Processing Result'
]

export interface RawTokenRow {
    'Existing Token': string
    'Replace with Token': string
    'Expiry Date': string
    Terminal: string
    'Update Type': string
    'Processing Result': string
}

export interface TokenRow {
    existingToken: string
    replaceWithToken: string
    expiryDate: string | null
    terminal: string
    updateType: UpdateType
    processingResult: ProcessingResult
}

export interface ParseResult {
    rows: TokenRow[]
    warnings: string[]
}

// ── helpers ─────────────────────────────────────────────────
const stripQuotes = (raw: string): string => {
    if (raw == null) return ''
    return String(raw)
        .trim()
        .replace(/^["']+|["']+$/g, '')
}

const parseExpiryDate = (raw: string): { value: string | null; warning: string | null } => {
    const cleaned = stripQuotes(raw)

    if (!cleaned || cleaned === '0000') {
        return { value: null, warning: null }
    }

    if (!/^\d{4}$/.test(cleaned)) {
        return { value: null, warning: `פורמט תאריך שגוי "${cleaned}" (נדרשות 4 ספרות YYMM)` }
    }

    const yy = cleaned.slice(0, 2)
    const mm = cleaned.slice(2, 4)
    const monthNum = parseInt(mm, 10)

    if (monthNum < 1 || monthNum > 12) {
        return { value: null, warning: `חודש שגוי בתאריך "${cleaned}" (התקבל חודש ${mm})` }
    }

    return { value: `${mm}/${yy}`, warning: null }
}

const normalizeType = (raw: string): string => {
    if (raw == null) return ''
    return stripQuotes(raw).toLowerCase()
}

// ── main ────────────────────────────────────────────────────
export const parseTokenRows = (rawRows: RawTokenRow[]): ParseResult => {
    const rows: TokenRow[] = []
    const warnings: string[] = []

    if (!Array.isArray(rawRows)) {
        return { rows, warnings }
    }

    for (let i = 0; i < rawRows.length; i++) {
        const raw = rawRows[i]
        if (!raw) continue

        const updateType = normalizeType(raw['Update Type'])
        const processingResult = normalizeType(raw['Processing Result'])

        if (updateType !== 'blocked' && updateType !== 'unfrozen') {
            continue
        }

        if (processingResult !== 'applied' && processingResult !== 'skipped') {
            warnings.push(`שורה ${i + 1}: ערך לא תקין ב-Processing Result: "${raw['Processing Result']}"`)
            continue
        }

        // Skipped rows are sent to the backend; applied rows are normally discarded,
        // except the unfrozen + applied combination which is also kept.
        const isSkipped = processingResult === 'skipped'
        const isUnfrozenApplied = updateType === 'unfrozen' && processingResult === 'applied'
        if (!isSkipped && !isUnfrozenApplied) {
            continue
        }

        const expiry = parseExpiryDate(raw['Expiry Date'])
        if (expiry.warning) {
            warnings.push(`שורה ${i + 1}: ${expiry.warning}`)
        }

        rows.push({
            existingToken: stripQuotes(raw['Existing Token']),
            replaceWithToken: stripQuotes(raw['Replace with Token']),
            expiryDate: expiry.value,
            terminal: stripQuotes(raw['Terminal']),
            updateType: updateType as UpdateType,
            processingResult: processingResult as ProcessingResult
        })
    }

    return { rows, warnings }
}
